import { useMemo, useRef, useState } from "preact/hooks";
import { pick, sum } from "lodash-es";
import { clsx } from "clsx";
import useClickAway from "react-use/esm/useClickAway";
import useLocalStorage from "@brickme/frontend-common/src/hooks/use-local-storage.ts";
import { useCurrentBrickedBuild } from "@brickme/frontend-common/src/picture-build/picture-build-context.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import CrossIcon from "~/components/icons/cross.tsx";
import {
	useSelectedCountryCode,
	useShopifyBrickProducts,
	useAvailableCountries,
	useStoreAddToCartUrl,
} from "~/hooks/region/context.tsx";
import { getColourBricksToBuy, packSize } from "~/utils/colour-bricks.ts";
import Button from "~/components/button.tsx";
import type { MoneyV2 } from "~/shopify-api/types.generated.ts";
import { formatMoney } from "~/shopify-api/money.ts";
import BrickIcon from "./brick-icon.tsx";
import IconTaggedText from "./icon-tagged-text.tsx";
import DownIcon from "./down-icon.tsx";
import { usePalettes } from "../editor-provider.tsx";
import AmountPill from "./amount-pill.tsx";
import handleImage from "./summary-handle.png?width=118&height=17&format=webp&as=metadata";
import classes from "./colours-cost-summary.module.css";

type ColoursCostSummaryProps = {
	readonly headerContentId: string;
	readonly open: boolean | "cart";
	readonly onChangeOpen: (newOpen: boolean) => void;
};

function ColoursCostSummary({
	headerContentId,
	open,
	onChangeOpen,
}: ColoursCostSummaryProps) {
	const t = useTranslator();
	const build = useCurrentBrickedBuild();
	const { kitsPalette, kitPlusExtrasPalette } = usePalettes();
	const brickProducts = useShopifyBrickProducts();
	const countries = useAvailableCountries();
	const { selectedCountry, setUserSelectedCountryCode } =
		useSelectedCountryCode();
	const brickCounts = build?.brickCounts;
	const orderBricks = useMemo(() => {
		const bricks = getColourBricksToBuy(
			brickCounts,
			brickProducts,
			kitsPalette,
			kitPlusExtrasPalette,
		).sort((c1, c2) => c1.brick.identifier - c2.brick.identifier);
		return {
			brickCounts: bricks,
			totalAmount: sum(bricks.map((c) => c.count)),
			totalPackBricksAmount: sum(bricks.map((c) => c.packBricksCount)),
			totalPacksAmount: sum(bricks.map((c) => c.packCount)),
			totalPrice:
				bricks.length === 0
					? undefined
					: bricks.reduce(
							(previous, brick): MoneyV2 | undefined => {
								const { packCount, shopifyVariant } = brick;
								if (shopifyVariant === undefined) {
									console.warn(`No product found for brick`, brick);
									return previous;
								}

								const { price } = shopifyVariant;
								const current = previous ?? {
									amount: "0",
									currencyCode: price.currencyCode,
								};
								if (current.currencyCode !== price.currencyCode) {
									throw new Error(
										`Received mixed currency codes ${current.currencyCode} and ${price.currencyCode}`,
									);
								}
								return {
									amount: (
										parseFloat(current.amount) +
										packCount * parseFloat(price.amount)
									).toString(),
									currencyCode: current.currencyCode,
								};
							},
							undefined as MoneyV2 | undefined,
						),
		};
	}, [brickCounts, brickProducts]);

	const [hideBricksNotice, setHideBricksNotice] = useLocalStorage<boolean>({
		key: "hideOrderBricksNotice",
		defaultValue: false,
		parse: JSON.parse,
		stringify: JSON.stringify,
	});

	// Add to cart
	const colourBricksRequired = getColourBricksToBuy(
		brickCounts,
		brickProducts,
		kitsPalette,
		kitPlusExtrasPalette,
	);
	const addToCartParams = new URLSearchParams();
	for (const { shopifyVariant, packCount } of colourBricksRequired) {
		if (shopifyVariant) {
			addToCartParams.append(
				"addProductItem",
				JSON.stringify({
					id: shopifyVariant.id,
					quantity: packCount,
				}),
			);
		}
	}
	const baseAddToCartUrl = useStoreAddToCartUrl();
	const addToCartUrl = `${baseAddToCartUrl}?${addToCartParams.toString()}`;

	// Cost drop down
	const [currencyOpen, setCurrencyOpen] = useState(false);
	const dropdownRef = useRef(null);
	useClickAway(dropdownRef, () => {
		setCurrencyOpen(false);
	});

	return (
		<div
			className={clsx(
				classes["colours-cost-summary"],
				open && classes["colours-cost-summary-open"],
			)}
		>
			<div className={classes["colours-cost-summary-header-container"]}>
				<div className={classes["colours-cost-summary-header"]}>
					<div
						id={headerContentId}
						className={classes["colours-cost-summary-header-inner"]}
					>
						<div
							ref={dropdownRef}
							className={clsx(
								classes["colours-cost-summary-header-side"],
								classes["colours-cost-summary-header-price"],
								classes["dropdown"],
								currencyOpen && classes["dropdown-open"],
							)}
						>
							<button
								type="button"
								className={classes["dropdown-button"]}
								onClick={() => setCurrencyOpen((p) => !p)}
								disabled={countries === undefined || countries.length <= 1}
							>
								<IconTaggedText
									isOpen={currencyOpen}
									icon={<DownIcon />}
									prefixText={
										orderBricks.totalPrice
											? orderBricks.totalPrice.currencyCode
											: undefined
									}
									text={
										orderBricks.totalPrice
											? formatMoney(orderBricks.totalPrice)
											: "--"
									}
								/>
							</button>
							<div className={classes["dropdown-content"]}>
								{countries?.map((c) => (
									<button
										key={c.country.isoCode}
										type="button"
										onClick={() => {
											setUserSelectedCountryCode(c.country.isoCode);
											setCurrencyOpen(false);
										}}
										className={clsx(
											classes["dropdown-item"],
											c.country.isoCode === selectedCountry?.country?.isoCode &&
												classes["dropdown-item-selected"],
										)}
									>
										{c.country.name}
									</button>
								))}
							</div>
						</div>
						<button
							type="button"
							className={classes["toggle-summary"]}
							onClick={() => onChangeOpen(!open)}
						>
							{open ? t("Hide summary") : t("Show summary")}
						</button>
						<div
							className={clsx(
								classes["colours-cost-summary-header-side"],
								classes["colours-cost-summary-header-bricks"],
							)}
						>
							<IconTaggedText
								icon={<BrickIcon />}
								text={orderBricks.totalAmount.toLocaleString()}
							/>
						</div>
					</div>
				</div>
			</div>
			<div className={classes["body-positioner"]}>
				<div className={classes["body-container"]}>
					<div className={classes["body"]}>
						<div className={classes["body-inner"]}>
							<div className={classes["body-top"]}>
								<div className={classes["colours-heading"]}>
									{t("Colors")} ({orderBricks.brickCounts.length})
								</div>
								{open === "cart" && (
									<Button href={addToCartUrl} newWindow variant="blue">
										{t("Add to cart")}
									</Button>
								)}
							</div>
							{!hideBricksNotice && (
								<div className={classes["brick-packs-notice"]}>
									<div />
									<div>
										{t("Note: Bricks always come in")}{" "}
										<strong>
											{t("packs of {amount}", { amount: packSize.toString() })}
										</strong>
									</div>
									<button
										type="button"
										onClick={() => setHideBricksNotice(true)}
									>
										<CrossIcon size={12} />
									</button>
								</div>
							)}
							<table className={classes["brick-table"]}>
								<thead>
									<tr>
										<th className={classes["label-cell"]}>{t("Colors")}</th>
										<th className={classes["amount-cell"]}>{t("Amount")}</th>
										<th className={classes["amount-cell"]}>{t("Order QTY")}</th>
									</tr>
								</thead>
								<tbody>
									{orderBricks.brickCounts.map((c, i) => (
										<tr
											key={c.brick.rgba}
											className={clsx(
												classes["brick-row"],
												i % 2 === 0
													? classes["brick-row-a"]
													: classes["brick-row-b"],
											)}
										>
											<td className={classes["label-cell"]}>
												<BrickIcon /> {t("Color")} {c.brick.identifier}
											</td>
											<td className={classes["amount-cell"]}>
												<AmountPill isOdd={i % 2 === 1}>{c.count}</AmountPill>
											</td>
											<td className={classes["amount-cell"]}>
												<AmountPill isOdd={i % 2 === 1}>
													{c.packBricksCount}
												</AmountPill>
											</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr className={classes["total-bricks-row"]}>
										<td className={classes["label-cell"]}>
											{t("Total bricks")}
										</td>
										<td className={classes["amount-cell"]}>
											<AmountPill isOdd>{orderBricks.totalAmount}</AmountPill>
										</td>
										<td className={classes["amount-cell"]}>
											<AmountPill isOdd>
												{orderBricks.totalPackBricksAmount}
											</AmountPill>
										</td>
									</tr>
									<tr>
										<td className={classes["label-cell"]}>
											{t("Total packs (of 100)")}
										</td>
										<td className={classes["amount-cell"]} />
										<td className={classes["amount-cell"]}>
											<AmountPill isOdd>
												{orderBricks.totalPacksAmount}
											</AmountPill>
										</td>
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
					<button
						type="button"
						className={classes["handle"]}
						onClick={() => onChangeOpen(!open)}
					>
						<img {...pick(handleImage, "src", "width", "height")} alt="" />
					</button>
				</div>
			</div>
		</div>
	);
}

export default ColoursCostSummary;
